/**
 * X-Digate Animations
 * Handles scroll-triggered animations and effects
 */

(function() {
    'use strict';

    // Configuration
    const config = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
    };

    // Elements to animate
    const animatedElements = document.querySelectorAll('[data-animate]');

    /**
     * Initialize animations
     */
    function init() {
        if (!animatedElements.length) return;

        // Check for reduced motion preference
        if (prefersReducedMotion()) {
            showAllElements();
            return;
        }

        setupIntersectionObserver();
        setupCounterAnimations();
    }

    /**
     * Check if user prefers reduced motion
     */
    function prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /**
     * Show all elements immediately (for reduced motion)
     */
    function showAllElements() {
        animatedElements.forEach(el => {
            el.classList.add('animated');
        });
    }

    /**
     * Setup Intersection Observer for scroll animations
     */
    function setupIntersectionObserver() {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const element = entry.target;
                    const delay = element.dataset.delay || 0;
                    
                    setTimeout(() => {
                        element.classList.add('animated');
                    }, parseInt(delay));
                    
                    // Unobserve after animation
                    observer.unobserve(element);
                }
            });
        }, config);

        animatedElements.forEach(el => {
            observer.observe(el);
        });
    }

    /**
     * Setup counter animations for statistics
     */
    function setupCounterAnimations() {
        const counters = document.querySelectorAll('.hero-stat-value, .use-case-stat-value');
        
        const counterObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    animateCounter(entry.target);
                    counterObserver.unobserve(entry.target);
                }
            });
        }, { threshold: 0.5 });

        counters.forEach(counter => {
            counterObserver.observe(counter);
        });
    }

    /**
     * Animate a counter element
     */
    function animateCounter(element) {
        const text = element.textContent;
        
        // Check if it's a simple number or contains other characters
        const match = text.match(/^(\d+(?:-\d+)?)/);
        
        if (!match) return;

        const targetText = match[0];
        const suffix = text.replace(targetText, '');
        
        // Handle range (e.g., "70-80")
        if (targetText.includes('-')) {
            const [start, end] = targetText.split('-').map(Number);
            animateRange(element, start, end, suffix);
        } else {
            // Handle single number
            const target = parseInt(targetText);
            animateNumber(element, target, suffix);
        }
    }

    /**
     * Animate a single number
     */
    function animateNumber(element, target, suffix) {
        const duration = 2000;
        const startTime = performance.now();
        
        function update(currentTime) {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function (ease-out)
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.round(target * eased);
            
            element.textContent = current + suffix;
            
            if (progress < 1) {
                requestAnimationFrame(update);
            } else {
                element.textContent = target + suffix;
            }
        }
        
        requestAnimationFrame(update);
    }

    /**
     * Animate a range (e.g., "70-80")
     */
    function animateRange(element, start, end, suffix) {
        const duration = 2000;
        const startTime = performance.now();
        
        function update(currentTime) {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function (ease-out)
            const eased = 1 - Math.pow(1 - progress, 3);
            const currentStart = Math.round(start * eased);
            const currentEnd = Math.round(end * eased);
            
            element.textContent = `${currentStart}-${currentEnd}${suffix}`;
            
            if (progress < 1) {
                requestAnimationFrame(update);
            } else {
                element.textContent = `${start}-${end}${suffix}`;
            }
        }
        
        requestAnimationFrame(update);
    }

    /**
     * Add stagger effect to a group of elements
     */
    function staggerElements(elements, baseDelay = 100) {
        elements.forEach((el, index) => {
            el.style.transitionDelay = `${baseDelay * index}ms`;
        });
    }

    /**
     * Create parallax effect for hero elements
     */
    function setupParallax() {
        const parallaxElements = document.querySelectorAll('[data-parallax]');
        
        if (!parallaxElements.length || prefersReducedMotion()) return;

        window.addEventListener('scroll', () => {
            const scrollY = window.pageYOffset;
            
            parallaxElements.forEach(el => {
                const speed = parseFloat(el.dataset.parallax) || 0.5;
                const yPos = -(scrollY * speed);
                el.style.transform = `translate3d(0, ${yPos}px, 0)`;
            });
        }, { passive: true });
    }

    /**
     * Chart bar animation
     */
    function animateChartBars() {
        const chartBars = document.querySelectorAll('.hero-chart-bar');
        
        if (!chartBars.length) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const bars = entry.target.querySelectorAll('.hero-chart-bar');
                    bars.forEach((bar, index) => {
                        bar.style.animationDelay = `${index * 100}ms`;
                    });
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.3 });

        const chartContainers = document.querySelectorAll('.hero-dashboard-chart');
        chartContainers.forEach(container => {
            observer.observe(container);
        });
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();
